import { SignInUnreachable } from '@/components/atoms/SignInUnreachable';
import { AuthenticatedApp } from '@/components/organisms/AuthenticatedApp';
import { useLanguagePreferenceReady } from '@/hooks/useLanguagePreferenceReady';
import { useRequireAuth } from '@/hooks/useRequireAuth';
import { useThemeBackend } from '@/hooks/useTheme';
import { HidePhiProvider } from '@/hooks/useHidePhi';
import { ConversationDraftProvider } from '@/pages/Conversation/Patientpedia/useConversationDraft';
import { AppBootProvider } from '@liza/ui/components/atoms/AppBoot';
import { createThemeProviderWithBackend } from '@liza/ui/components/atoms/createThemeProviderWithBackend';
import { createFileRoute } from '@tanstack/react-router';

// Component that integrates ThemeProvider with backend persistence
const ThemeProviderWithBackendIntegration = createThemeProviderWithBackend(useThemeBackend);

const RouteComponent = (): React.ReactNode => {
  const { isAuthenticated, signInUnreachable, retrySignIn } = useRequireAuth();
  const languageReady = useLanguagePreferenceReady(isAuthenticated);

  return (
    <ThemeProviderWithBackendIntegration>
      {signInUnreachable ? (
        <SignInUnreachable onRetry={retrySignIn} />
      ) : (
        /* The app can't mount before auth and the language preference resolve, and pages hold this
           same loading screen while their initial data loads (see useBootBlocker). */
        <AppBootProvider gatesOpen={isAuthenticated && languageReady}>
          <HidePhiProvider>
            <ConversationDraftProvider>
              <AuthenticatedApp />
            </ConversationDraftProvider>
          </HidePhiProvider>
        </AppBootProvider>
      )}
    </ThemeProviderWithBackendIntegration>
  );
};

export const Route = createFileRoute('/_auth')({
  component: RouteComponent,
  loader: () => ({ crumb: 'Home' }),
});
